import { useState, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Send, CheckCircle, Clock, XCircle, FileCheck, MessageSquare, Paperclip, Image, Upload, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fi } from "date-fns/locale";
import {
  CEMETERY_APPROVAL_STATUS_LABELS,
  CEMETERY_APPROVAL_STATUS_COLORS,
  type CemeteryApprovalStatus,
} from "@/config/order-constants";

interface CemeteryProof {
  id: string;
  file_path: string;
  file_name: string;
  version: number;
  created_at: string;
}

interface OrderCemeteryApprovalCardProps {
  orderId: string;
  orderNumber: string;
  requiresApproval: boolean;
  approvalStatus: CemeteryApprovalStatus;
  approvalRequestedAt: string | null;
  approvalRespondedAt: string | null;
  approvalNotes: string | null;
  approvalMessage?: string | null;
  approvalProofId?: string | null;
  cemetery: {
    id: string;
    name: string;
    contact_email: string | null;
    contact_name: string | null;
  } | null;
}

const DEFAULT_MESSAGE = `Hei,

Pyydämme ystävällisesti lupaa suorittaa seuraava työ hautausmaallanne.

Ystävällisin terveisin,
Kiviveistämö`;

export function OrderCemeteryApprovalCard({
  orderId,
  orderNumber,
  requiresApproval,
  approvalStatus,
  approvalRequestedAt,
  approvalRespondedAt,
  approvalNotes,
  approvalMessage,
  approvalProofId,
  cemetery,
}: OrderCemeteryApprovalCardProps) {
  
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [selectedProofId, setSelectedProofId] = useState<string>("none");
  const [proofPreviewUrl, setProofPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Fetch cemetery proofs from the new table
  const { data: proofs, refetch: refetchProofs } = useQuery({
    queryKey: ["cemetery-proofs", orderId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cemetery_proofs")
        .select("id, file_path, file_name, version, created_at")
        .eq("order_id", orderId)
        .order("version", { ascending: false });
      
      if (error) throw error;
      return (data || []) as CemeteryProof[];
    },
    enabled: showSendDialog,
  });

  // Get next version number
  const getNextVersion = () => {
    if (!proofs || proofs.length === 0) return 1;
    return Math.max(...proofs.map(p => p.version)) + 1;
  };

  // Upload cemetery proof
  const uploadProof = async (file: File) => {
    setIsUploading(true);
    try {
      const version = getNextVersion();
      const fileExt = file.name.split('.').pop();
      const filePath = `${orderId}/cemetery-v${version}.${fileExt}`;

      // Upload to cemetery-proofs bucket
      const { error: uploadError } = await supabase.storage
        .from("cemetery-proofs")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Create record in cemetery_proofs table
      const { data: user } = await supabase.auth.getUser();
      const { error: insertError } = await supabase
        .from("cemetery_proofs")
        .insert({
          order_id: orderId,
          file_path: filePath,
          file_name: file.name,
          file_size: file.size,
          version,
          created_by: user.user?.id,
        });

      if (insertError) throw insertError;

      await refetchProofs();
      toast.success("Vedos ladattu", {
        description: `Versio ${version} lisätty onnistuneesti.`,
      });
    } catch (error) {
      toast.error("Virhe", {
        description: error instanceof Error ? error.message : "Vedoksen lataus epäonnistui",
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Delete cemetery proof
  const deleteProofMutation = useMutation({
    mutationFn: async (proofId: string) => {
      const proof = proofs?.find(p => p.id === proofId);
      if (!proof) throw new Error("Vedosta ei löydy");

      // Delete from storage
      await supabase.storage.from("cemetery-proofs").remove([proof.file_path]);

      // Delete from database
      const { error } = await supabase
        .from("cemetery_proofs")
        .delete()
        .eq("id", proofId);

      if (error) throw error;
    },
    onSuccess: () => {
      refetchProofs();
      if (selectedProofId !== "none") {
        setSelectedProofId("none");
        setProofPreviewUrl(null);
      }
      toast.success("Vedos poistettu");
    },
    onError: (error: Error) => {
      toast.error("Virhe", {
        description: error.message || "Vedoksen poisto epäonnistui",
      });
    },
  });

  // Handle file input change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadProof(file);
    }
    e.target.value = "";
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      uploadProof(file);
    }
  };

  // Load proof preview when selected
  const loadProofPreview = async (proofId: string) => {
    if (proofId === "none") {
      setProofPreviewUrl(null);
      return;
    }
    
    const proof = proofs?.find(p => p.id === proofId);
    if (proof) {
      const { data } = await supabase.storage
        .from("cemetery-proofs")
        .createSignedUrl(proof.file_path, 3600);
      setProofPreviewUrl(data?.signedUrl || null);
    }
  };

  const handleProofChange = (value: string) => {
    setSelectedProofId(value);
    loadProofPreview(value);
  };

  const sendApprovalRequestMutation = useMutation({
    mutationFn: async ({ message, proofId }: { message: string; proofId: string | null }) => {
      const response = await supabase.functions.invoke("request-order-approval", {
        body: { 
          orderId, 
          message: message.trim() || undefined,
          proofId: proofId !== "none" ? proofId : undefined,
        },
      });
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order-detail", orderId] });
      setShowSendDialog(false);
      setMessage(DEFAULT_MESSAGE);
      setSelectedProofId("none");
      setProofPreviewUrl(null);
      toast.success("Hyväksyntäpyyntö lähetetty", {
        description: "Sähköposti lähetettiin seurakunnan yhteyshenkilölle.",
      });
    },
    onError: (error: Error) => {
      toast.error("Virhe", {
        description: error.message || "Hyväksyntäpyynnön lähetys epäonnistui",
      });
    },
  });

  const handleOpenSendDialog = () => {
    if (!cemetery?.contact_email) {
      toast.error("Sähköpostiosoite puuttuu", {
        description: "Hautausmaalle ei ole määritetty yhteystietoja. Lisää ne hautausmaan tietoihin.",
      });
      return;
    }
    setShowSendDialog(true);
  };

  const handleSendApprovalRequest = () => {
    sendApprovalRequestMutation.mutate({
      message,
      proofId: selectedProofId !== "none" ? selectedProofId : null,
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return format(new Date(dateString), "d.M.yyyy 'klo' HH:mm", { locale: fi });
  };

  const getStatusIcon = () => {
    switch (approvalStatus) {
      case "approved":
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case "pending":
        return <Clock className="h-5 w-5 text-amber-600" />;
      case "rejected":
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <FileCheck className="h-5 w-5 text-muted-foreground" />;
    }
  };

  // Don't show card if approval is not required
  if (!requiresApproval) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {getStatusIcon()}
              <CardTitle className="text-lg">Seurakunnan hyväksyntä</CardTitle>
            </div>
            <Badge
              variant="outline"
              className={CEMETERY_APPROVAL_STATUS_COLORS[approvalStatus]}
            >
              {CEMETERY_APPROVAL_STATUS_LABELS[approvalStatus]}
            </Badge>
          </div>
          {cemetery && (
            <CardDescription>
              Hautausmaa: {cemetery.name}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status information */}
          {approvalStatus === "pending" && approvalRequestedAt && (
            <div className="text-sm text-muted-foreground space-y-2">
              <p>Pyyntö lähetetty: {formatDate(approvalRequestedAt)}</p>
              {cemetery?.contact_email && (
                <p>Vastaanottaja: {cemetery.contact_email}</p>
              )}
              
              {/* Show sent message preview */}
              {approvalMessage && (
                <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                    <MessageSquare className="h-3 w-3" />
                    Lähetetty viesti
                  </div>
                  <p className="text-sm line-clamp-3">{approvalMessage}</p>
                </div>
              )}
              
              {/* Show if proof was attached */}
              {approvalProofId && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Paperclip className="h-3 w-3" />
                  Vedos liitetty pyyntöön
                </div>
              )}
            </div>
          )}

          {approvalStatus === "approved" && approvalRespondedAt && (
            <div className="text-sm text-green-700 bg-green-50 rounded-md p-3">
              <p className="font-medium">Hyväksytty: {formatDate(approvalRespondedAt)}</p>
              {approvalNotes && <p className="mt-1">{approvalNotes}</p>}
            </div>
          )}

          {approvalStatus === "rejected" && (
            <div className="text-sm text-red-700 bg-red-50 rounded-md p-3">
              <p className="font-medium">Hylätty: {formatDate(approvalRespondedAt)}</p>
              {approvalNotes && <p className="mt-1">Syy: {approvalNotes}</p>}
            </div>
          )}

          {/* Actions */}
          {(approvalStatus === "not_required" || approvalStatus === "awaiting_request") && (
            <div className="space-y-3">
              {approvalStatus === "awaiting_request" && (
                <div className="text-sm text-blue-700 bg-blue-50 rounded-md p-3">
                  Hyväksyntäpyyntöä ei ole vielä lähetetty seurakunnalle.
                </div>
              )}
              <Button
                onClick={handleOpenSendDialog}
                disabled={!cemetery?.contact_email}
                className="w-full"
              >
                <Send className="h-4 w-4 mr-2" />
                Lähetä hyväksyntäpyyntö
              </Button>
            </div>
          )}

          {approvalStatus === "pending" && (
            <div className="space-y-3">
              <div className="text-center text-sm text-muted-foreground">
                Odottaa seurakunnan vastausta...
              </div>
              <Button
                variant="outline"
                onClick={handleOpenSendDialog}
                disabled={!cemetery?.contact_email}
                className="w-full"
              >
                <Paperclip className="h-4 w-4 mr-2" />
                Hallitse vedoksia / Lähetä uusi pyyntö
              </Button>
            </div>
          )}

          {approvalStatus === "rejected" && (
            <Button
              variant="outline"
              onClick={handleOpenSendDialog}
              disabled={!cemetery?.contact_email}
              className="w-full"
            >
              <Send className="h-4 w-4 mr-2" />
              Lähetä uusi pyyntö
            </Button>
          )}

          {!cemetery?.contact_email && approvalStatus !== "approved" && (
            <p className="text-xs text-amber-600">
              Hautausmaalle ei ole määritetty sähköpostiosoitetta. 
              Lisää yhteystiedot hautausmaan asetuksiin.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Send Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Lähetä hyväksyntäpyyntö</DialogTitle>
            <DialogDescription>
              Vastaanottaja: {cemetery?.contact_email}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Cemetery proofs management section */}
            <div className="space-y-3">
              <Label>Seurakunnan vedokset</Label>
              
              {/* Upload area */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`
                  border-2 border-dashed rounded-lg p-4 text-center transition-colors
                  ${isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25"}
                  ${isUploading ? "opacity-50 pointer-events-none" : "cursor-pointer hover:border-primary/50"}
                `}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {isUploading ? (
                  <div className="flex items-center justify-center gap-2 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Ladataan...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1 text-muted-foreground">
                    <Upload className="h-6 w-6" />
                    <span className="text-sm">Vedä kuva tähän tai klikkaa ladataksesi</span>
                  </div>
                )}
              </div>

              {/* Existing proofs list */}
              {proofs && proofs.length > 0 && (
                <div className="space-y-2">
                  {proofs.map((proof) => (
                    <div
                      key={proof.id}
                      className="flex items-center justify-between p-2 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Image className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                        <span className="text-sm truncate">
                          Vedos v{proof.version}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(proof.created_at), "d.M.yyyy", { locale: fi })}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 flex-shrink-0"
                        onClick={() => deleteProofMutation.mutate(proof.id)}
                        disabled={deleteProofMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Proof selection for sending */}
            <div className="space-y-2">
              <Label>Liitä vedos pyyntöön</Label>
              {proofs && proofs.length > 0 ? (
                <Select value={selectedProofId} onValueChange={handleProofChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Valitse vedos..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ei liitettä</SelectItem>
                    {proofs.map((proof) => (
                      <SelectItem key={proof.id} value={proof.id}>
                        <div className="flex items-center gap-2">
                          <Image className="h-3 w-3" />
                          <span>Vedos v{proof.version}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-muted-foreground py-2">
                  Ei vedoksia. Lataa vedos yllä.
                </p>
              )}
              
              {/* Proof preview */}
              {proofPreviewUrl && (
                <div className="mt-2 border rounded-lg overflow-hidden relative">
                  <img
                    src={proofPreviewUrl}
                    alt="Vedoksen esikatselu"
                    className="w-full h-auto max-h-48 object-contain bg-muted"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-1 right-1 h-6 w-6 bg-background/80"
                    onClick={() => {
                      setSelectedProofId("none");
                      setProofPreviewUrl(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Message field */}
            <div className="space-y-2">
              <Label htmlFor="approval-message">Viesti seurakunnalle</Label>
              <Textarea
                id="approval-message"
                placeholder="Kirjoita viesti seurakunnalle..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
              />
              <p className="text-xs text-muted-foreground">
                Viesti näytetään seurakunnalle hyväksyntäsivulla.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSendDialog(false)}
              disabled={sendApprovalRequestMutation.isPending}
            >
              Peruuta
            </Button>
            <Button
              onClick={handleSendApprovalRequest}
              disabled={sendApprovalRequestMutation.isPending}
            >
              {sendApprovalRequestMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Lähetä
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
